// Lead Drop Form Variants Configuration

import { setLeadDropForm1 } from "@/store/slices/university";
import type { UniversityState } from "@/store/slices/university";
import { Form_4, SegmentWorkbenchForm } from "@/components/organisms/admin/forms";
import { LEAD_DROP_FORM_VARIANTS } from "../../sectionTypes";
import { LEAD_DROP_FORM_1_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_1";
import { LEAD_DROP_FORM_2_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_2";
import { LEAD_DROP_FORM_3_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_3";
import { LEAD_DROP_FORM_4_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_4";
import { LEAD_DROP_FORM_10_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_10";
import {
  LEADDROP_MOBILE_STRIP_HELPER_TEXT,
  LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS,
  MOBILE_STRIP_LEADDROP_CTA_FIELDS,
} from "../../fields/leadDropForm/leadDropForm_segmented";
import {
  LEAD_DROP_FORM_8_CONSTANTS,
  buildLeadDrop8DynamicDefinitions,
} from "../../fields/leadDropForm/leadDropForm_8";
import { LEAD_DROP_FORM_9_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_9";
import { LEAD_DROP_FORM_5002_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_5002";
import { LEAD_DROP_FORM_5018_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_5018";
import { LEAD_DROP_FORM_2001_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_2001";
import { updateLeadDropForm } from "@/api/admin/leadDropForm";
import type { VariantConfig } from "../types";
import { COMMON_CONSTANTS } from "../types";

/**
 * Variant 8 has no `dynamic_field_definitions` JSON field in the admin UI; the
 * explicit Name/Email/Qualification/Program label/placeholder/required keys (stored
 * in `additional_fields`) are recompiled into that JSON on every save so the
 * unchanged Leaddrop8 component can read it.
 */
const withCompiledLeadDrop8Dynamic = (payload: any) => {
  // Strip any top-level `dynamic_field_definitions` (it is not a DB column; it
  // lives inside additional_fields and is recompiled below).
  const { dynamic_field_definitions: _leaked, ...rest } = payload || {};
  const additional =
    rest && typeof rest.additional_fields === "object" && rest.additional_fields
      ? rest.additional_fields
      : {};
  return {
    ...rest,
    additional_fields: {
      ...additional,
      dynamic_field_definitions: buildLeadDrop8DynamicDefinitions(additional),
    },
  };
};

export const LEAD_DROP_FORM_VARIANTS_CONFIG: Record<string, VariantConfig> = {
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_1]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_1,
    name: "Lead Drop Form 1",
    description: "Lead Drop Form 1",
    componentFields: LEAD_DROP_FORM_1_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_1_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2,
    name: "Lead Drop Form 2",
    description: "Lead Drop Form 2",
    componentFields: LEAD_DROP_FORM_2_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_2_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  // ── Net-new opt-in variants (ported from the multi-tenant line) ─────────────
  // Variant 3 — inline "Drop a query" form. On submit it writes the lead straight
  // to the CMS database via POST /leads/direct (createLeadDirect): NO OTP and NO
  // ERP/LSQ push. Works identically in standalone and multi-tenant.
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_3]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_3,
    name: "Lead Drop Form 3",
    description:
      "Drop a query (name, email, phone, details). Saves to the CMS database only (no OTP, no ERP/LSQ) via /leads/direct.",
    componentFields: LEAD_DROP_FORM_3_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_3_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  // Variant 4 — same single-step form as form 1 but NO OTP. On submit the saved
  // lead is pushed to CRM via POST /leads/sync-drop4. Standalone always routes to
  // ERP; LSQ is used only on multi-tenant microsite hosts (isMultiTenant gate in
  // services/leadDrop4.js). The CMS destination field is honoured only in
  // multi-tenant mode.
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_4]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_4,
    name: "Lead Drop Form 4",
    description:
      "Same single-step form as form 1 but no OTP; on submit pushes the lead to CRM (/leads/sync-drop4). Standalone → ERP; LSQ only for multi-tenant microsites.",
    componentFields: LEAD_DROP_FORM_4_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_4_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_10]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_10,
    name: "Lead Drop Form 10",
    description:
      "Inline lead form collecting full name, phone, email, highest qualification and area of interest with OTP verification.",
    componentFields: LEAD_DROP_FORM_10_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_10_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6,
    name: "Multi-step Lead Drop Form",
    description: "Phone → OTP → two steps — configured in four tabs",
    componentFields: LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.MERGED_FIELDS_CONFIG,
    segmentDefinitions: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.SEGMENT_WORKBENCH_SEGMENTS],
    globalSegmentFields: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.LEAD_STRIP_GLOBAL_FIELDS],
    globalLeadDropMobileStripFields: [...MOBILE_STRIP_LEADDROP_CTA_FIELDS],
    globalLeadDropMobileStripHelpText: LEADDROP_MOBILE_STRIP_HELPER_TEXT,
    metadataFields: [],
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: SegmentWorkbenchForm,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_8]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_8,
    name: "Lead Drop Form 8",
    description:
      "IIT-style phone-first inline lead form. Twin of Lead Drop Popup 8. Secondary fields (Name / Email / Qualification / Program) configured via explicit label & placeholder.",
    componentFields: LEAD_DROP_FORM_8_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_8_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: (id: number, data: any) =>
      updateLeadDropForm(id, withCompiledLeadDrop8Dynamic(data)),
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_9]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_9,
    name: "Lead Drop Form 9",
    description:
      "Apply Now inline lead form. Twin of Lead Drop Popup 9 — single-step form with an optional countdown, OTP, and configurable last cards.",
    componentFields: LEAD_DROP_FORM_9_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_9_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2001]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2001,
    name: "Lead Drop Form 2001",
    description:
      "Inline twin of Lead Drop Popup 2001 — the same card embedded in the page, without the close button. Single step with the OTP revealed inline; a course marked Certification Programme continues to a background step and LMS signup.",
    componentFields: LEAD_DROP_FORM_2001_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_2001_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_5002]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_5002,
    name: "Lead Drop Form 5002",
    description:
      "BDU-style inline lead form. Twin of Lead Drop Popup 5002 — single-step with the OTP revealed inline, rendered as one narrow column without the side image.",
    componentFields: LEAD_DROP_FORM_5002_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_5002_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
  [LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_5018]: {
    id: LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_5018,
    name: "Lead Drop Form 5018",
    description:
      "KUK hero lead card — labelled Name, Phone, Email and Course shown all at once. Apply Now hands those details to Lead Drop Popup 5018, which verifies the number and shows the success card, so the OTP and success screens are configured on the popup.",
    componentFields: LEAD_DROP_FORM_5018_CONSTANTS.LEAD_DROP_FORM_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_FORM_5018_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropForm1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_form_1,
    updateMethod: updateLeadDropForm,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop form saved successfully" },
  },
};

export const getLeadDropFormVariantDetails = (variantId: string) =>
  LEAD_DROP_FORM_VARIANTS_CONFIG[variantId];

